/**
 * HTML serializer — converts ContentUnit back to HTML strings (raw-only).
 *
 * Every indexed tag carries a `raw:<opening tag>` style. The serializer
 * routes the stored opening tag through the shared allowlist sanitizer and
 * derives the close tag from the embedded tag name. Void elements get no
 * close tag. Text segments are HTML-escaped (ContentUnit.text holds decoded
 * Unicode — see MarkupParser.decodeEntities), so serializer output is inert
 * even when the translation value is attacker-controlled. `ContentUnit.text`
 * itself is never modified — auto_key hashing is unaffected.
 *
 * @module content/serializer/HTMLSerializer
 */

import type { ISerializer } from './ISerializer.js';
import type { ContentUnit } from '../types.js';
import { extractTagName, readRawStyle, VOID_TAGS } from '../canonical-tag.js';
import { escapeHtml, sanitizeOpeningTag } from '../sanitizer/HTMLSanitizer.js';

/**
 * Serializer that converts ContentUnit format back to HTML strings.
 *
 * @example
 * ```typescript
 * const serializer = new HTMLSerializer();
 * serializer.serialize({
 *   text: 'Read our <1>Terms</1>.',
 *   styles: { '1': ['raw:<b>'] }
 * });
 * // → 'Read our <b>Terms</b>.'
 * ```
 */
export class HTMLSerializer implements ISerializer<string> {
  serialize(unit: ContentUnit): string {
    const { text, styles } = unit;
    const tagPattern = /<(\/?)?(\d+)(\/)?>/g;

    let result = '';
    let lastIndex = 0;
    let match;

    while ((match = tagPattern.exec(text)) !== null) {
      result += escapeHtml(text.slice(lastIndex, match.index));

      const [fullMatch, closingSlash, indexStr] = match;
      const styleList = styles[indexStr] ?? [];
      const rawOpen = readRawStyle(styleList);
      const safeOpen = rawOpen === null ? null : sanitizeOpeningTag(rawOpen);

      if (rawOpen && safeOpen !== null) {
        const tagName = extractTagName(rawOpen);
        const isVoid = VOID_TAGS.has(tagName);

        if (closingSlash) {
          if (!isVoid) result += `</${tagName}>`;
        } else {
          // Both <N> and <N/> emit the opening tag. For void tags that's the
          // complete element.
          result += safeOpen;
        }
      } else {
        // No raw style, or the sanitizer rejected the element — preserve the
        // indexed marker (parses as inert text) so the problem is visible
        // rather than silently dropping content, and never emit a close tag
        // for a rejected element.
        result += fullMatch;
      }

      lastIndex = match.index + fullMatch.length;
    }

    result += escapeHtml(text.slice(lastIndex));
    return result;
  }
}
